import React from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cn } from "../utils";
import { typography } from "../tokens";

const VARIANTS = {
  success: {
    icon: CheckCircle2,
    ring: "border-emerald-200/80 bg-white",
    iconClass: "text-emerald-600",
    bar: "bg-emerald-500",
  },
  error: {
    icon: AlertCircle,
    ring: "border-red-200/80 bg-white",
    iconClass: "text-red-600",
    bar: "bg-red-500",
  },
  info: {
    icon: Info,
    ring: "border-blue-200/80 bg-white",
    iconClass: "text-blue-600",
    bar: "bg-blue-500",
  },
};

function ToastItem({ toast, onDismiss }) {
  const cfg = VARIANTS[toast.variant] || VARIANTS.info;
  const Icon = cfg.icon;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "toast-item pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border p-4 shadow-lg",
        "shadow-[0_8px_30px_rgba(59,59,59,0.12)]",
        cfg.ring
      )}
    >
      <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", cfg.iconClass)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {toast.title && (
          <p className="text-gray-900 font-medium" style={typography.bodyMedium}>
            {toast.title}
          </p>
        )}
        <p className={cn("text-gray-600", toast.title ? "mt-0.5" : "")} style={typography.body}>
          {toast.message}
        </p>
        <div className="mt-2.5 h-0.5 w-full overflow-hidden rounded-full bg-gray-100">
          <div className={cn("toast-item-bar h-full rounded-full", cfg.bar)} />
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Fixed toast stack — portaled to document.body. */
export function ToastViewport({ toasts, onDismiss }) {
  if (!toasts.length) return null;

  const node = (
    <div
      className="toast-viewport pointer-events-none fixed right-4 top-[4.5rem] z-[400000] flex w-[min(100vw-2rem,24rem)] flex-col gap-2.5 md:right-6"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );

  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}
